import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { ChannelType } from 'aws-sdk/clients/pinpoint';
import { NavigateFunction } from 'react-router-dom';

import { put } from '../../common/api/api-helpers';
import { IS_IOS } from '../../common/constants';

type Platform = 'web' | 'ios' | 'android';
export const PLATFORM: Platform = Capacitor.getPlatform() as Platform;
const ANDROID_DEFAULT_CHANNEL_ID = 'se_charge_general';

async function ensureAndroidDefaultNotificationChannel() {
  if (PLATFORM !== 'android') return;

  await PushNotifications.createChannel({
    id: ANDROID_DEFAULT_CHANNEL_ID,
    name: 'General',
    description: 'General app notifications',
    importance: 5,
    visibility: 1,
    sound: 'default',
  });
}

/**
 * Sets up the application to receive push notifications.
 */
let currentUserLocale: string | null = null;

const onRegistration = async ({ value }: Token) => {
  // Send our token to the API for registration in our own database
  const platformToChannelType: Record<Platform, ChannelType | null> = {
    web: null,
    android: 'GCM',
    ios: 'APNS',
    // @NOTE: uncomment this when working in the development environment, or PNs will not be received.
    // ios: 'APNS_SANDBOX'
  };

  const channelType = platformToChannelType[PLATFORM];
  if (!channelType) {
    console.warn('Push notification registration skipped: no channel type for platform', PLATFORM);
    return;
  }
  if (!value) {
    console.error('Push notification registration failed: received empty FCM/APNS token');
    return;
  }

  try {
    await put('/v1/notifications/token', {
      token: value,
      channel_type: channelType,
      app_name: 'SCHNEIDER_CHARGE',
      locale: currentUserLocale,
    });
  } catch (e) {
    console.error('Push notification token registration API call failed:', e);
  }
};

/**
 * @NOTE
 * This method of listening for deep links is used because setting the app up for deep links as per the
 * Capacitor documentation is not actually possible, because serving a file without an extension from the public
 * directory does not work at all. There is no known solution to this issue and as such deep links cannot work
 * with this app in iOS.
 * Instead, we can just hook onto an action performed on a PN to redirect the user - this is a hacky
 * solution, and if a solution to the above issue with the `apple-app-site-association` file is found at some
 * point the preferred listener for `appUrlOpen` should be implemented instead. Ejecting from CRA at some point
 * might also give more flexibility for accessing static resources, but this is currently out of scope.
 *
 * See: https://bit.ly/3xyrDpl
 */
const onPushNotificationAction = async (action: ActionPerformed, navigate: NavigateFunction) => {
  if (action.actionId === 'tap') {
    let route: string | undefined;
    let url: string | undefined;

    if (IS_IOS) {
      route = action.notification?.data?.aps?.alert?.data?.route;
      url = action.notification?.data?.aps?.alert?.data?.url;
    } else {
      route = action.notification?.data?.route;
      url = action.notification?.data?.url;
    }

    if (route) {
      navigate(route);
      return;
    }
    if (url) {
      await Browser.open({ url: url });
      return;
    }
  }
};

const onNotification = (_notification: PushNotificationSchema) => {
  // 'pushNotificationReceived' -> good for when you might want to update the app state based on some event
};

export async function setupPushNotifications(navigate: NavigateFunction, userLocale?: string) {
  // Store the user's locale for use in the onRegistration callback
  if (userLocale) {
    currentUserLocale = userLocale;
  }

  await ensureAndroidDefaultNotificationChannel();

  let permStatus = await PushNotifications.checkPermissions();

  if (['prompt', 'prompt-with-rationale'].includes(permStatus.receive)) {
    permStatus = await PushNotifications.requestPermissions();
  }

  if (permStatus.receive !== 'granted') {
    console.warn(`Push notifications permission not granted: ${permStatus.receive}`);
    return;
  }

  // Remove any existing listeners before re-registering to prevent stacking
  // if this function is called more than once during the app lifecycle.
  await PushNotifications.removeAllListeners();

  PushNotifications.addListener('registration', onRegistration);
  PushNotifications.addListener('pushNotificationReceived', onNotification);
  PushNotifications.addListener('registrationError', (error) => {
    console.error('Push notification registration error:', error);
  });
  PushNotifications.addListener('pushNotificationActionPerformed', (action) =>
    onPushNotificationAction(action, navigate)
  );

  await PushNotifications.register();
}
